import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { CheckCircle2, User, Phone, Building2, MapPin, Gift } from "lucide-react";
import CityAutocomplete from "@/components/CityAutocomplete";

export default function Register() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { settings } = useSettings();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", phone: "", sppg_name: "", address: "", city: "",
    referral_code: params.get("ref") || "",
  });
  const [otp, setOtp] = useState("");
  const [otpDebug, setOtpDebug] = useState("");
  const [loading, setLoading] = useState(false);
  const waDisplay = settings.wa_official_display || "0851-4788-8865";

  const submitInit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.sppg_name || !form.address || !form.city) {
      toast.error("Semua kolom wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/partner/register/init", form);
      setOtpDebug(r.data.otp_debug);
      toast.success(`OTP dikirim via WhatsApp ke ${form.phone}`);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim OTP");
    }
    setLoading(false);
  };

  const submitVerify = async () => {
    if (otp.length !== 6) { toast.error("OTP harus 6 digit"); return; }
    setLoading(true);
    try {
      await api.post("/partner/register/verify", { phone: form.phone, otp });
      toast.success("Registrasi berhasil!");
      localStorage.setItem("ti_partner_phone", form.phone);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP salah");
    }
    setLoading(false);
  };

  return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] flex items-center justify-center py-14 px-4">
      <div className="w-full max-w-lg fade-up">
        <div className="mb-6 text-center">
          <div className="pill-cyan mx-auto w-fit mb-3">DAFTAR PEMBELI</div>
          <h1 className="font-display text-3xl font-bold text-white">Bergabung dengan tokoinduk.com</h1>
          <p className="text-slate-400 text-sm mt-2">SPPG · Warung · Resto · Catering · Pasar · Toko — semua bisa daftar. OTP dikirim ke WhatsApp Anda.</p>
        </div>

        <Card className="glass glow-border bg-transparent rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            {[1,2,3].map(n => (
              <div key={n} className="flex-1 flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono-metric font-bold ${
                  step >= n ? "bg-cyan-400 text-slate-950" : "bg-slate-800 text-slate-500"
                }`}>{step > n ? <CheckCircle2 className="w-4 h-4"/> : n}</div>
                {n < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > n ? "bg-cyan-400" : "bg-slate-800"}`}/>}
              </div>
            ))}
          </div>

          {step === 1 && (
            <form onSubmit={submitInit} className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Nama Lengkap</Label>
                <div className="relative mt-1.5">
                  <User className="w-4 h-4 absolute left-3 top-3 text-slate-500"/>
                  <Input data-testid="reg-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Pak Yono" className="pl-9 bg-slate-900/60 border-slate-700 text-white h-11"/>
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">No. WhatsApp</Label>
                <div className="relative mt-1.5">
                  <Phone className="w-4 h-4 absolute left-3 top-3 text-slate-500"/>
                  <Input data-testid="reg-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} placeholder="+62 812-xxxx-xxxx" className="pl-9 bg-slate-900/60 border-slate-700 text-white h-11"/>
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Nama SPPG / Warung / Resto / Toko</Label>
                <div className="relative mt-1.5">
                  <Building2 className="w-4 h-4 absolute left-3 top-3 text-slate-500"/>
                  <Input data-testid="reg-sppg" value={form.sppg_name} onChange={e=>setForm({...form, sppg_name:e.target.value})} placeholder="SPPG Cilamaya 02 / Warung Bu Ani / Resto Sederhana" className="pl-9 bg-slate-900/60 border-slate-700 text-white h-11"/>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Kota / Kabupaten</Label>
                  <div className="mt-1.5">
                    <CityAutocomplete testid="reg-city" value={form.city} onChange={v => setForm({...form, city: v})} placeholder="Karawang, Bandung, Jakarta…"/>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric">Alamat Lengkap</Label>
                  <div className="relative mt-1.5">
                    <MapPin className="w-4 h-4 absolute left-3 top-3 text-slate-500"/>
                    <Input data-testid="reg-address" value={form.address} onChange={e=>setForm({...form, address:e.target.value})} placeholder="Jl. Raya Cilamaya No. 12" className="pl-9 bg-slate-900/60 border-slate-700 text-white h-11"/>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-metric flex items-center gap-1">
                  <Gift className="w-3 h-3 text-amber-400"/> Kode Referal Sales <span className="text-slate-500 lowercase font-sans normal-case">(opsional)</span>
                </Label>
                <Input data-testid="reg-referral" value={form.referral_code} onChange={e=>setForm({...form, referral_code:e.target.value.toUpperCase()})} placeholder="ADIT-1234" className="mt-1.5 bg-slate-900/60 border-slate-700 text-white h-11 font-mono-metric"/>
              </div>
              <Button data-testid="reg-submit-btn" disabled={loading} type="submit" className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">
                {loading ? "Mengirim..." : "Kirim OTP"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <div className="space-y-6 text-center">
              <div>
                <div className="font-display text-lg font-bold text-white">Verifikasi Kode OTP</div>
                <div className="text-sm text-slate-400 mt-1">Kode dikirim via <span className="text-emerald-300">WhatsApp</span> ke <span className="text-cyan-300 font-mono-metric">{form.phone}</span></div>
                <div className="text-[10px] text-slate-500 mt-1 font-mono-metric">Belum terima? Chat manual ke {waDisplay}</div>
              </div>
              <div className="flex justify-center">
                <InputOTP data-testid="otp-input" maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => (
                      <InputOTPSlot key={i} index={i} className="bg-slate-900/60 border-slate-700 text-white text-lg h-12 w-12"/>
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpDebug && (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 font-mono-metric">
                  [DEMO] Kode OTP: <span data-testid="otp-debug" className="font-bold text-amber-200">{otpDebug}</span>
                </div>
              )}
              <Button data-testid="otp-verify-btn" disabled={loading} onClick={submitVerify} className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold">
                {loading ? "Memverifikasi..." : "Verifikasi & Aktifkan Akun"}
              </Button>
              <button onClick={() => setStep(1)} className="text-xs text-slate-400 hover:text-cyan-300">← Ubah data</button>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-6">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4"/>
              <h3 className="font-display text-2xl font-bold text-white">Registrasi Berhasil!</h3>
              <p className="text-slate-400 mt-2">Akun mitra <span className="text-cyan-300">{form.sppg_name}</span> aktif.</p>
              <Button data-testid="post-reg-chat-btn" onClick={() => nav("/chat")} className="mt-6 h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-8">
                Buka AI Chat Ordering
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
